/**
 * The `/admin` authorization map: which platform role may reach which SECTION.
 *
 * ## Why this file is the whole boundary (read before editing)
 *
 * Hasura roles are NOT the boundary inside `/admin`. All 28 admin pages, every
 * admin server action and all 24 `/api/admin/*` route handlers read through
 * `adminGqlExecutor` / `adminRead`, i.e. the Hasura ADMIN SECRET, which bypasses
 * every permission in the database. Exactly one file queries under the caller's
 * own JWT (`admin/tenants/TenantsClient.tsx`), and `role: admin` appears zero
 * times across the 384 metadata YAMLs because Hasura's `admin` is the reserved
 * superuser, not a granted role.
 *
 * So a new Hasura role grants a hire NOTHING here. Authorization in `/admin` is
 * entirely ENTRY-POINT authorization, across three independent classes:
 *
 *   1. RSC page render   -> `requirePlatform(section)` in the page/segment
 *   2. Server action POST -> `requireAdminAction(section)` as the first statement
 *   3. Route handler     -> `requireApiAdmin(request, section)`
 *
 * Missing any one of them is a FULL bypass, because what sits behind them is a
 * superuser transport with no in-database backstop. The Hasura grants still
 * matter -- for the one browser-side query, for impersonation traffic, and as
 * defence in depth -- but they are not the isolation mechanism.
 *
 * `admin-sections.coverage.test.ts` walks every `page.tsx` and `route.ts` on
 * disk and fails if any of them is unmapped or ungated. Do not weaken it.
 *
 * ## Deliberately NOT server-only
 *
 * This module holds pure data and pure functions with no imports, so
 * `AdminSidebar` (a client component) can source its per-item `roles` from the
 * same constant the server gate decides on. Nav and gate cannot drift when they
 * read one map. Never add a server import here.
 */

/**
 * A TaskyInn STAFF role. Not a tenant role -- these are platform personas whose
 * Hasura grants are cross-tenant (`filter: {}`).
 *
 * `admin` is the incumbent superuser persona; the other three are the scoped
 * roles that replace it for new hires (GH #1393).
 */
export type PlatformRole = 'admin' | 'support' | 'billing_admin' | 'onboarding'

/** Every platform role, in descending privilege. */
export const PLATFORM_ROLES: readonly PlatformRole[] = [
  'admin',
  'support',
  'billing_admin',
  'onboarding',
] as const

/** The scoped roles -- everything except the incumbent superuser persona. */
export const SCOPED_PLATFORM_ROLES: readonly PlatformRole[] = [
  'support',
  'billing_admin',
  'onboarding',
] as const

/**
 * A gateable slice of `/admin`. One section = one authorization decision; every
 * page, action and route resolves to exactly one.
 *
 * `tenantDetail` is separate from `tenants` on purpose: the tenants LIST is a
 * directory, but `/api/admin/tenants/[tenantId]` returns that tenant's
 * recognized GL revenue, its owner's contact details and its subscription --
 * the same class of commercial data that keeps `/admin/insights` and
 * `/api/admin/portfolio-report` admin-only.
 */
export type AdminSection =
  | 'root'
  | 'tenants'
  | 'tenantDetail'
  | 'users'
  | 'invites'
  | 'billing'
  | 'accounting'
  | 'support'
  | 'system'

/**
 * Section -> the platform roles allowed to reach it.
 *
 * `root` MUST stay exactly `['admin']`: `sectionForPath()` falls back to `root`
 * for any path it does not recognize, so `root` doubles as the fail-closed
 * answer. Widening it would silently widen every unmapped path along with it.
 * `admin-sections.test.ts` pins this.
 *
 * `/api/admin/impersonate*` and `/api/admin/users*` (section `users`) are never
 * widened -- impersonation is excluded from every scoped role's remit.
 */
export const ADMIN_SECTIONS: Record<AdminSection, readonly PlatformRole[]> = {
  // Overview, tenant insights, portfolio report, settings.
  // `/admin/insights` runs per-guest reservation analysis and
  // `/api/admin/portfolio-report` returns EVERY tenant's revenue, VAT and OTA
  // commissions. No scoped role gets those.
  root: ['admin'],
  // The tenants directory -- read-only for anyone but `admin` (the enable/
  // disable mutation is hidden client-side AND refused server-side).
  tenants: ['admin', 'support'],
  // Per-tenant revenue / owner / subscription drilldown. Admin only.
  tenantDetail: ['admin'],
  // Platform user administration and impersonation. Admin only, always.
  users: ['admin'],
  invites: ['admin', 'onboarding'],
  billing: ['admin', 'billing_admin'],
  // TaskyInn's OWN books (the HQ ledger). Same persona as billing, but the
  // Hasura grants are disjoint: Accounting n Billing = {}.
  accounting: ['admin', 'billing_admin'],
  support: ['admin', 'support'],
  // Server monitor, webhook monitor, translations, roadmap.
  system: ['admin'],
}

/** Where each role lands when it opens `/admin` with no deeper destination. */
export const ADMIN_LANDING_PATH: Record<PlatformRole, string> = {
  admin: '/admin',
  support: '/admin/support-tickets',
  billing_admin: '/admin/billing',
  onboarding: '/admin/invite-codes',
}

interface SectionRoute {
  /** Path prefix, matched on SEGMENT boundaries (never a bare startsWith). */
  path: string
  /** Match this path and nothing beneath it. */
  exact?: boolean
  section: AdminSection
}

/**
 * Path -> section. Longest match wins, so a more specific entry can override a
 * broader one regardless of the order written here.
 *
 * Both entry surfaces live in one table on purpose: a page and the route its
 * client half calls must not end up in different sections by accident.
 */
const SECTION_ROUTES: readonly SectionRoute[] = [
  // --- pages: app/(platform)/admin/**/page.tsx -----------------------------
  { path: '/admin', exact: true, section: 'root' },
  { path: '/admin/insights', section: 'root' },
  { path: '/admin/portfolio-report', section: 'root' },
  { path: '/admin/settings', section: 'root' },
  { path: '/admin/tenants', section: 'tenants' },
  { path: '/admin/users', section: 'users' },
  { path: '/admin/invite-codes', section: 'invites' },
  { path: '/admin/billing', section: 'billing' },
  { path: '/admin/pricing', section: 'billing' },
  { path: '/admin/accounting', section: 'accounting' },
  { path: '/admin/support-tickets', section: 'support' },
  // Support desk v2 (#2577): the notification / automation switches and SLA targets are the
  // founder's decision, not a support agent's. Longest match makes this subtree admin-only.
  { path: '/admin/support-tickets/settings', section: 'root' },
  // Cross-tenant reservation lookup (read-only) for support calls.
  { path: '/admin/reservations', section: 'support' },
  // Credit-note lane N2: releases of a group credit note (the audited support exit).
  { path: '/admin/invoice-releases', section: 'support' },
  { path: '/admin/server-monitor', section: 'system' },
  { path: '/admin/webhooks', section: 'system' },
  { path: '/admin/translations', section: 'system' },
  { path: '/admin/roadmap', section: 'system' },

  // --- route handlers: app/api/admin/**/route.ts ---------------------------
  { path: '/api/admin/portfolio-report', section: 'root' },
  { path: '/api/admin/provision-subscription', section: 'root' },
  { path: '/api/admin/housekeeping', section: 'root' },
  { path: '/api/admin/impersonate', section: 'users' },
  { path: '/api/admin/users', section: 'users' },
  { path: '/api/admin/tenants', section: 'tenantDetail' },
  { path: '/api/admin/billing', section: 'billing' },
  { path: '/api/admin/zatca', section: 'billing' },
  { path: '/api/admin/accounting', section: 'accounting' },
  { path: '/api/admin/server-monitor', section: 'system' },
]

/** True when `pathname` is `prefix` or sits beneath it on a segment boundary. */
function matches(pathname: string, route: SectionRoute): boolean {
  if (pathname === route.path) return true
  if (route.exact) return false
  return pathname.startsWith(route.path + '/')
}

/**
 * Resolve a pathname to its section.
 *
 * FAIL-CLOSED: an unrecognized path resolves to `root`, i.e. `['admin']`. A new
 * page or route is therefore admin-only until it is added here on purpose --
 * the opposite of the usual "allow by default and remember to lock it down".
 *
 * Query strings and trailing slashes are normalized away first, so
 * `/admin/users/?x=1` cannot dodge the `users` entry.
 */
export function sectionForPath(pathname: string): AdminSection {
  const clean = (pathname.split('?')[0] ?? '').split('#')[0]?.replace(/\/+$/, '') || '/'
  let best: SectionRoute | null = null
  for (const route of SECTION_ROUTES) {
    if (!matches(clean, route)) continue
    if (!best || route.path.length > best.path.length) best = route
  }
  return best ? best.section : 'root'
}

/** Narrow an arbitrary role string to a platform role. */
export function isPlatformRole(role: string | null | undefined): role is PlatformRole {
  return !!role && (PLATFORM_ROLES as readonly string[]).includes(role)
}

/**
 * The role-switcher override, narrowed to a platform role that the token
 * genuinely allows -- or null.
 *
 * This is the TOKEN-VERIFIABLE half of the "effective platform role" the /admin
 * gate acts on: an `x-hasura-role` override is honored ONLY when it is
 *   (1) a platform role, and
 *   (2) present in the JWT's `x-hasura-allowed-roles` (Hasura-signed, unforgeable).
 * A tenant role is refused here -- honoring one would tenant-scope the
 * cross-tenant admin surface, the exact 2026-07-12 scope-collapse. A role the
 * user does not hold is refused because it is not in allowed-roles.
 *
 * The THIRD guard -- an ACTIVE `platform_staff` row for (user_id, this role) --
 * needs a database round-trip and lives server-side only
 * (`hasActivePlatformStaffRole`). This pure function is shared by the server gate
 * and by client render paths (nav/redirect decisions); a returned role is a
 * CANDIDATE, made effective only after the server confirms the membership row.
 * Returns null (never throws) so callers fall back to the token default role.
 */
export function honoredPlatformOverride(
  override: string | null | undefined,
  allowedRoles: readonly string[],
): PlatformRole | null {
  if (!isPlatformRole(override)) return null
  if (!allowedRoles.includes(override)) return null
  return override
}

/**
 * The EFFECTIVE platform role for a `/admin` request — the persona the caller is
 * genuinely acting as. The single source of truth for the precedence, kept pure
 * so it can be pinned exactly (the server wiring only supplies the inputs).
 *
 *   effective = (override passes ALL THREE guards) ? override : defaultRole
 *
 * The override wins ONLY when it is a platform role (guard 1), held in the token
 * (guard 2, via `honoredPlatformOverride`) AND backed by an active membership row
 * (guard 3, `overrideHasMembership` — the caller resolves this against
 * `platform_staff`). If ANY guard fails, the override is discarded and the token
 * DEFAULT role stands: a bad override is ignored, never a lock-out. It is a plain
 * fallback (`candidate ?? default`), NOT the reverse — swapping the precedence
 * would let a stale/removed default silently win over a legitimate switch, or
 * (worse) let the default mask a section the override should have narrowed to.
 */
export function effectivePlatformRole(
  override: string | null | undefined,
  defaultRole: string | null,
  allowedRoles: readonly string[],
  overrideHasMembership: boolean,
): string | null {
  const candidate = honoredPlatformOverride(override, allowedRoles)
  if (candidate && overrideHasMembership) return candidate
  return defaultRole ?? null
}

/** May `role` reach `section`? Fails closed on a non-platform / absent role. */
export function sectionAllows(section: AdminSection, role: string | null | undefined): boolean {
  if (!isPlatformRole(role)) return false
  return ADMIN_SECTIONS[section].includes(role)
}

/** The sections `role` may reach, in declaration order. Used by the nav. */
export function sectionsForRole(role: string | null | undefined): AdminSection[] {
  if (!isPlatformRole(role)) return []
  return (Object.keys(ADMIN_SECTIONS) as AdminSection[]).filter((s) =>
    ADMIN_SECTIONS[s].includes(role),
  )
}

/** Where `role` should be sent when it opens the portal. `/admin` if unknown. */
export function landingPathForRole(role: string | null | undefined): string {
  return isPlatformRole(role) ? ADMIN_LANDING_PATH[role] : '/admin'
}
